import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./pickers.css";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const WEEK = ["L", "M", "M", "J", "V", "S", "D"];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

export type Choice = { value: string; label: string; color?: string };

export function ChoiceField({
  value,
  options,
  placeholder = "Choisir",
  onChange,
}: {
  value: string;
  options: Choice[];
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  const pop = usePop();
  const current = options.find((item) => item.value === value);
  return (
    <div ref={pop.wrap} className={pop.open ? "crm-ctrl crm-sel is-open" : "crm-ctrl crm-sel"}>
      <button ref={pop.btn} type="button" className="crm-ctrl__btn" aria-expanded={pop.open} onClick={pop.toggle}>
        <span className="crm-ctrl__ico" aria-hidden>
          {current?.color ? <span className="crm-ctrl__dot" style={{ background: current.color }} /> : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
            </svg>
          )}
        </span>
        <span className={current ? "crm-ctrl__lab" : "crm-ctrl__lab is-empty"}>{current?.label || placeholder}</span>
        <Chevron />
      </button>
      {pop.panel(
        <div className="crm-sel__list" role="listbox">
          {options.map((item) => (
            <button
              key={item.value || "empty"}
              type="button"
              className={item.value === value ? "crm-sel__opt is-on" : "crm-sel__opt"}
              onClick={() => {
                onChange(item.value);
                pop.close();
              }}
            >
              {item.color ? <span className="crm-ctrl__dot" style={{ background: item.color }} /> : null}
              {item.label}
            </button>
          ))}
        </div>,
        "crm-sel__panel",
      )}
    </div>
  );
}

export function MultiChoiceField({
  values,
  options,
  emptyLabel = "Aucune",
  onChange,
}: {
  values: string[];
  options: Choice[];
  emptyLabel?: string;
  onChange: (values: string[]) => void;
}) {
  const pop = usePop();
  const picked = options.filter((item) => values.includes(item.value));
  const label = picked.length > 0 ? picked.map((item) => item.label).join(", ") : emptyLabel;
  return (
    <div ref={pop.wrap} className={pop.open ? "crm-ctrl crm-sel is-open" : "crm-ctrl crm-sel"}>
      <button ref={pop.btn} type="button" className="crm-ctrl__btn" aria-expanded={pop.open} onClick={pop.toggle}>
        <span className="crm-ctrl__ico" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
          </svg>
        </span>
        <span className={picked.length > 0 ? "crm-ctrl__lab" : "crm-ctrl__lab is-empty"}>{label}</span>
        <Chevron />
      </button>
      {pop.panel(
        <div className="crm-sel__list" role="listbox">
          <button
            type="button"
            className={values.length === 0 ? "crm-sel__opt is-on" : "crm-sel__opt"}
            onClick={() => { onChange([]); pop.close(); }}
          >
            {emptyLabel}
          </button>
          {options.map((item) => {
            const on = values.includes(item.value);
            return (
              <button
                key={item.value}
                type="button"
                className={on ? "crm-sel__opt is-on" : "crm-sel__opt"}
                onClick={() => onChange(on ? values.filter((value) => value !== item.value) : [...values, item.value])}
              >
                {item.label}
              </button>
            );
          })}
        </div>,
        "crm-sel__panel",
      )}
    </div>
  );
}

export function DateField({ value, onChange }: { value: string; onChange: (iso: string) => void }) {
  const pop = usePop();
  const selected = parseIso(value);
  const [cursor, setCursor] = useState(() => selected ?? new Date());

  useEffect(() => {
    if (pop.open) setCursor(selected ?? new Date());
  }, [pop.open, value]);

  const days = useMemo(() => cells(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const today = toIso(new Date());

  return (
    <div ref={pop.wrap} className={pop.open ? "crm-ctrl crm-date is-open" : "crm-ctrl crm-date"}>
      <button ref={pop.btn} type="button" className="crm-ctrl__btn" aria-expanded={pop.open} onClick={pop.toggle}>
        <span className="crm-ctrl__ico" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="4" y="5" width="16" height="15" rx="2" />
            <path d="M8 3v4M16 3v4M4 10h16" />
          </svg>
        </span>
        <span className="crm-ctrl__lab">{labelDate(value)}</span>
        <Chevron />
      </button>
      {pop.panel(
        <div role="dialog">
          <div className="crm-date__nav">
            <button type="button" aria-label="Mois précédent" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</button>
            <strong>{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</strong>
            <button type="button" aria-label="Mois suivant" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</button>
          </div>
          <div className="crm-date__week">
            {WEEK.map((letter, index) => <span key={`${letter}-${index}`}>{letter}</span>)}
          </div>
          <div className="crm-date__grid">
            {days.map((day) => (
              <button
                key={day.iso + (day.other ? "-o" : "")}
                type="button"
                className={[day.other ? "is-other" : "", day.iso === today ? "is-today" : "", day.iso === value ? "is-on" : ""].filter(Boolean).join(" ")}
                onClick={() => {
                  onChange(day.iso);
                  pop.close();
                }}
              >
                {day.day}
              </button>
            ))}
          </div>
          <button type="button" className="crm-date__today" onClick={() => { onChange(today); pop.close(); }}>Aujourd’hui</button>
        </div>,
        "crm-date__panel",
      )}
    </div>
  );
}

export function TimeField({ value, onChange, step = 5 }: { value: string; onChange: (hhmm: string) => void; step?: 5 | 15 }) {
  const pop = usePop();
  const minutes = step === 15 ? [0, 15, 30, 45] : MINUTES;
  const current = parts(value, step);

  function setAt(hour: number, minute: number, close: boolean) {
    onChange(`${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
    if (close) pop.close();
  }

  return (
    <div ref={pop.wrap} className={pop.open ? "crm-ctrl crm-time is-open" : "crm-ctrl crm-time"}>
      <button ref={pop.btn} type="button" className="crm-ctrl__btn" aria-expanded={pop.open} onClick={pop.toggle}>
        <span className="crm-ctrl__ico" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="12" cy="12" r="8.5" />
            <path d="M12 7.5V12l3.2 2" />
          </svg>
        </span>
        <span className="crm-ctrl__lab">{`${String(current.h).padStart(2, "0")} h ${String(current.m).padStart(2, "0")}`}</span>
        <Chevron />
      </button>
      {pop.panel(
        <div role="dialog">
          <p className="crm-time__cap">Heure</p>
          <div className="crm-time__grid">
            {Array.from({ length: 24 }, (_, hour) => (
              <button key={hour} type="button" className={hour === current.h ? "is-on" : ""} onClick={() => setAt(hour, current.m, false)}>
                {String(hour).padStart(2, "0")}
              </button>
            ))}
          </div>
          <p className="crm-time__cap">Minutes</p>
          <div className={step === 15 ? "crm-time__mins crm-time__mins--quarter" : "crm-time__mins"}>
            {minutes.map((minute) => (
              <button key={minute} type="button" className={minute === current.m ? "is-on" : ""} onClick={() => setAt(current.h, minute, true)}>
                {String(minute).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>,
        "crm-time__panel",
      )}
    </div>
  );
}

function usePop() {
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  function place() {
    const rect = btn.current?.getBoundingClientRect();
    const box = panel.current?.getBoundingClientRect();
    if (!rect) return;
    const width = panel.current?.classList.contains("crm-sel__panel") ? Math.max(rect.width, 240) : box?.width || 262;
    const height = box?.height || 280;
    let left = rect.left;
    let top = rect.bottom + 6;
    if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
    if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 6);
    setPos({ top, left, width: rect.width });
  }

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent) {
      const target = event.target as Node;
      if (!wrap.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  function panelNode(children: ReactNode, kind: string) {
    if (!open) return null;
    return createPortal(
      <div ref={panel} className={`crm-ctrl__panel ${kind}`} style={{ top: pos.top, left: pos.left, width: kind === "crm-sel__panel" ? pos.width || undefined : undefined }}>
        {children}
      </div>,
      document.body,
    );
  }

  return { wrap, btn, open, toggle: () => setOpen((next) => !next), close: () => setOpen(false), panel: panelNode };
}

function Chevron() {
  return (
    <span className="crm-ctrl__chev" aria-hidden>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </span>
  );
}

function toIso(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseIso(iso: string): Date | null {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function labelDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return "Choisir";
  return `${day}/${month}/${year}`;
}

function cells(year: number, month: number) {
  const first = new Date(year, month, 1);
  const pad = (first.getDay() + 6) % 7;
  const out: { iso: string; day: number; other: boolean }[] = [];
  for (let i = 0; i < pad; i += 1) {
    const date = new Date(year, month, i - pad + 1);
    out.push({ iso: toIso(date), day: date.getDate(), other: true });
  }
  const last = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= last; day += 1) out.push({ iso: toIso(new Date(year, month, day)), day, other: false });
  while (out.length % 7 !== 0) {
    const date = new Date(year, month + 1, out.length - pad - last + 1);
    out.push({ iso: toIso(date), day: date.getDate(), other: true });
  }
  return out;
}

function parts(hhmm: string, step = 5): { h: number; m: number } {
  const match = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  let hour = match ? Math.min(23, Number(match[1])) : 9;
  let minute = match ? Math.min(59, Number(match[2])) : 0;
  minute = Math.round(minute / step) * step;
  if (minute >= 60) {
    minute = 0;
    hour = (hour + 1) % 24;
  }
  return { h: hour, m: minute };
}
